import { Component, Input, OnInit, OnChanges, SimpleChanges, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { maskitoTransform } from '@maskito/core';
import { MasksService } from 'src/app/services/masks/masks.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-post-input',
  templateUrl: './post-input.component.html',
  styleUrls: ['./post-input.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PostInputComponent implements OnInit, OnChanges {
  constructor(
    private masksService: MasksService,
    private router: Router,
    private utilsService: UtilsService
  ) {}
  @Input() visible: boolean = false;
  @Input() disabled: boolean = false;
  @Input() inputControl: FormControl | null = null;
  @Input() invalid: boolean = false;
  @Input() field: any;
  imageUrls: String[] = [];

  numberMask: any = this.masksService.getMask('number');
  nameMask = this.masksService.getMask('name');
  textMask = this.masksService.getMask('');

  ngOnInit() {
    this.initializeValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Cuando cambia el inputControl, actualizar los valores
    if (changes['inputControl'] && this.inputControl) {
      this.initializeValues();
    }
  }

  private initializeValues() {
    // Inicializar valores basados en el inputControl
    if (this.field.formControlName === 'images') {
      this.imageUrls = Array.isArray(this.inputControl?.value)
        ? this.inputControl.value
        : [];
    }
  }

  setMask(form: any) {
    let mask: any;
    mask = this.textMask;
    return mask;
  }

  applyMask(value: any) {
    if (!this.inputControl) return;

    let maskedValue = value;
    switch (this.field.formControlName) {
      case 'value':
        // Permitir solo dígitos y un único separador decimal (coma o punto). Limitar a 2 decimales.
        const s = String(value ?? '').replace(/[^\d.,]/g, '');
        // Elegir separador decimal preferente: si hay coma, usar coma; si no, si hay punto, usar punto
        const sep = s.includes(',') ? ',' : (s.includes('.') ? '.' : '');
        if (!sep) {
          maskedValue = s.replace(/[.,]/g, '');
        } else {
          const firstIdx = s.indexOf(sep);
          let intPart = s.slice(0, firstIdx).replace(/[^\d]/g, '');
          let decRaw = s.slice(firstIdx + 1).replace(/[^\d]/g, '');
          const dec = decRaw.slice(0, 2);
          if (!intPart) intPart = '0';
          maskedValue = dec.length > 0 ? `${intPart}${sep}${dec}` : `${intPart}${sep}`;
        }
        break;
      default:
        maskedValue = maskitoTransform(value, this.masksService.getMask(''));
    }

    this.inputControl.setValue(maskedValue, { emitEvent: true });
    this.inputControl.updateValueAndValidity();
  }

  // Formatea el valor actual del control como moneda en EUR con 2 decimales y símbolo €
  formatCurrency() {
    if (!this.inputControl || this.field.formControlName !== 'value') return;
    const raw = (this.inputControl.value ?? '').toString();
    if (!raw) return;
    const num = this.parseToNumber(raw);
    if (isNaN(num)) return;
    const formatted = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    this.inputControl.setValue(formatted, { emitEvent: false });
  }

  // Quita el formato de moneda dejando un número con punto decimal para edición
  unformatCurrency() {
    if (!this.inputControl || this.field.formControlName !== 'value') return;
    const current = (this.inputControl.value ?? '').toString();
    if (!current) return;
    const num = this.parseToNumber(current);
    if (isNaN(num)) {
      this.inputControl.setValue('', { emitEvent: false });
    } else {
      // Mostrar con hasta 2 decimales si aplica, sin símbolo
      this.inputControl.setValue(num % 1 === 0 ? String(num) : num.toFixed(2), { emitEvent: false });
    }
  }

  // Convierte cadenas con €, puntos, comas a número JS
  // Maneja correctamente separadores de miles y decimales tanto con coma como con punto
  private parseToNumber(value: string): number {
    if (value === null || value === undefined || value === '') return NaN;
    let v = value.toString().trim();
    // Eliminar espacios y símbolos de moneda
    v = v.replace(/\s/g, '').replace(/[€£$]/g, '');

    const hasComma = v.indexOf(',') !== -1;
    const hasDot = v.indexOf('.') !== -1;

    if (hasComma && hasDot) {
      const lastDot = v.lastIndexOf('.');
      const lastComma = v.lastIndexOf(',');
      if (lastDot > lastComma) {
        // dot es separador decimal -> eliminar comas como miles
        v = v.replace(/,/g, '');
      } else {
        // coma es separador decimal -> eliminar puntos como miles y convertir coma a punto
        v = v.replace(/\./g, '').replace(/,/g, '.');
      }
    } else if (hasComma && !hasDot) {
      // solo coma -> asumir coma decimal
      v = v.replace(/,/g, '.');
    } else if (!hasComma && hasDot) {
      // solo punto -> puede haber múltiples puntos (p.ej. errores): conservar solo el último como decimal
      const parts = v.split('.');
      if (parts.length > 2) {
        const last = parts.pop();
        v = parts.join('') + '.' + last;
      }
    }

    const n = parseFloat(v);
    return isNaN(n) ? NaN : n;
  }

  navigate(path: string) {
    this.router.navigate([path]);
  }

  async handleImageUpload() {
    let url = await this.utilsService.takePicture();
    this.imageUrls.push(url);
    this.inputControl?.setValue(this.imageUrls);
  }

  setSelection(value: any) {
    this.inputControl?.setValue(value);
  }

  removeImage(index: number): void {
    this.imageUrls.splice(index, 1);
    this.inputControl?.setValue(this.imageUrls);
  }
}
